import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { ActivityCategory } from "@sp-crm/core";
import { InputFormBlock } from "components/app/input-form-block";
import { ActivityRowsList } from "components/details-page-v-next/activity-rows";
import { ActivityFilterChips } from "components/details-page-v-next/recent-activities";
import {
    NOTES_FILTER_KEY,
    RecentActivityFilterKey,
} from "components/details-page-v-next/recent-activities-feed";
import { Header } from "components/header";
import { BlankSlate } from "components/ui/blank-slate";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import * as React from "react";
import { SharedActivityFeedItem, SharedHistoryEntry } from "./shared-lead-history";

const INITIAL_VISIBLE_COUNT = 3;
const SHOW_MORE_INCREMENT = 10;

const feedCategories: readonly ActivityCategory[] = ["call", "sms", "email", "other"];

export const sharedHistoryFilterKey = (
    entry: SharedHistoryEntry,
): RecentActivityFilterKey => {
    switch (entry.activityType) {
        case "NoteCreate":
        case "NoteDelete":
            return NOTES_FILTER_KEY;
        case "OutgoingCall":
            return "call";
        case "SmsSent":
            return "sms";
        case "MessageSent":
            return "email";
        default:
            return "other";
    }
};

const filterHistory = (
    history: SharedHistoryEntry[],
    selected: RecentActivityFilterKey[],
): SharedHistoryEntry[] =>
    selected.length === 0
        ? history
        : history.filter(entry => selected.includes(sharedHistoryFilterKey(entry)));

export const SharedRecentActivities: React.FC<{ history: SharedHistoryEntry[] }> = ({
    history,
}) => {
    const [selected, setSelected] = React.useState<RecentActivityFilterKey[]>([]);
    const [visibleCount, setVisibleCount] = React.useState(INITIAL_VISIBLE_COUNT);

    const toggle = React.useCallback((key: RecentActivityFilterKey) => {
        setSelected(current =>
            current.includes(key)
                ? current.filter(existing => existing !== key)
                : [...current, key],
        );
        setVisibleCount(INITIAL_VISIBLE_COUNT);
    }, []);

    const clear = React.useCallback(() => {
        setSelected([]);
        setVisibleCount(INITIAL_VISIBLE_COUNT);
    }, []);

    const showMore = React.useCallback(
        () => setVisibleCount(count => count + SHOW_MORE_INCREMENT),
        [],
    );

    const filtered = filterHistory(history, selected);

    return (
        <InputFormBlock>
            <Header>Recent Activities</Header>
            <ActivityFilterChips
                categories={feedCategories}
                selected={selected}
                onToggle={toggle}
                onClear={clear}
            />
            {filtered.length === 0 ? (
                <div className="mt-6">
                    <BlankSlate>No activity yet.</BlankSlate>
                </div>
            ) : (
                <>
                    <ActivityRowsList className="mt-6" testId="shared-recent-activities">
                        {filtered.slice(0, visibleCount).map(entry => (
                            <SharedActivityFeedItem key={entry.id} entry={entry} />
                        ))}
                    </ActivityRowsList>
                    {filtered.length > visibleCount ? (
                        <InteractiveTextLink
                            className="mt-3 inline-flex items-center gap-1 text-sm font-medium"
                            onClick={showMore}>
                            <ChevronDownIcon aria-hidden="true" className="h-4 w-4" />
                            Show more
                        </InteractiveTextLink>
                    ) : null}
                </>
            )}
        </InputFormBlock>
    );
};
